// utils/learning/kgCache.js
// ★ 知识图谱 IndexedDB 缓存
// key = `${kgid}|${level}`，value = 完整接口返回（data.kg 等字段）
// 生命周期：localStorage/progressStore 一致 —— 图谱删除时一并删除缓存

const DB_NAME = 'kg_learning_cache';
const STORE = 'graphs';
const DB_VERSION = 1;
const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 天过期，防止后端图谱更新后一直用旧数据

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  // 连接失败时重置，下次重试
  _dbPromise.catch(() => { _dbPromise = null; });
  return _dbPromise;
}

function mkKey(kgid, level) {
  return `${kgid || ''}|${level || ''}`;
}

function tx(mode) {
  return openDB().then(db => db.transaction(STORE, mode).objectStore(STORE));
}

/** 读缓存：命中且未过期返回 { kgid, level, data, cachedAt }，否则 null */
export async function getCachedGraph(kgid, level) {
  try {
    const store = await tx('readonly');
    return await new Promise((resolve) => {
      const req = store.get(mkKey(kgid, level));
      req.onsuccess = () => {
        const row = req.result;
        if (!row) return resolve(null);
        if (Date.now() - row.cachedAt > MAX_AGE) return resolve(null); // 过期视为未命中
        resolve(row);
      };
      req.onerror = () => resolve(null); // 出错不阻塞，走后端
    });
  } catch (e) {
    return null;
  }
}

/** 写缓存（接口成功后调用） */
export async function setCachedGraph(kgid, level, data) {
  try {
    const store = await tx('readwrite');
    return await new Promise((resolve, reject) => {
      const req = store.put({ key: mkKey(kgid, level), kgid, level, data, cachedAt: Date.now() });
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn('[kgCache] 写缓存失败:', e);
  }
}

/** 删除某个图谱缓存（删除图谱时调用） */
export async function removeCachedGraph(kgid, level) {
  try {
    const store = await tx('readwrite');
    return await new Promise((resolve) => {
      const req = store.delete(mkKey(kgid, level));
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
    });
  } catch (e) {
    return false;
  }
}

/** 清空全部图谱缓存（可选：导入/覆盖数据等场景使用） */
export async function clearGraphCache() {
  try {
    const store = await tx('readwrite');
    return await new Promise((resolve) => {
      const req = store.clear();
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
    });
  } catch (e) {
    return false;
  }
}
